import { useEffect, useMemo, useState } from "react";
import { istPauschal } from "@maestro/pricing";
import type { PriceBreakdown } from "@maestro/pricing";
import type { OfferItem, OfferOption, OfferSection } from "./types";
import { Card, Icon, Input } from "./components";
import { Row } from "./Row";
import { centsToEuros, money, parseGermanDecimal, parseGermanEurosToCents } from "./format";
import { vatLabel } from "../lib/builder-helpers";
import { rabattVorschauZeile, type RabattModus } from "../lib/rabatt-vorschau";

/**
 * Zusammenfassungs-Karte (Region 4e, Konzept § 2.5) — Summen, USt-Aufschlüsselung
 * (`data-testid="builder-gesamtsumme"` bleibt erhalten), Referenz-Hinweis bei KI-Varianten,
 * der Preis-anpassen-Regler. Aus `builder.tsx` (Schritt 12, Einheit F, letzter
 * Extraktionsschritt des Loops).
 *
 * Phase B (Konzept § 3/§ 5): `discountMode`/`discountPreviewRaw` sind rein lokaler UI-State
 * dieser Karte (Konzept § 3 Tabelle Zeile 3 — aendert sich bei jedem Tastendruck, betrifft nur
 * die eigene Anzeige, kein anderer Konsument liest sie ausserhalb des unten stehenden
 * `discountPreviewLine`) und wandern hier vollstaendig hinein; `discountPreviewLine` zieht mit
 * um, weil es ausschliesslich aus diesen beiden plus `breakdown.subtotalCents` (bereits Prop)
 * abgeleitet wird — kein zweiter Rechenweg, `rabattVorschauZeile` bleibt exakt dieselbe reine
 * Funktion aus `lib/rabatt-vorschau.ts`. `setDiscount`/`setTargetPrice` bleiben bewusst Props:
 * beide tragen ihr H-12-Gate (`if (!activeId || !editable) return;`) und schreiben ueber die
 * Autosave-/Options-Kette im Parent (Konzept § 3 Tabelle Zeile 1) — nicht rein lokal.
 */
export function ZusammenfassungsKarte({
  option,
  sectionSubtotals,
  breakdown,
  activeId,
  adjustment,
  setDiscount,
  setTargetPrice,
  setError,
}: {
  option: OfferOption | null;
  sectionSubtotals: { key: OfferSection; label: string; cents: number }[];
  breakdown: PriceBreakdown;
  activeId: string | null;
  adjustment: OfferItem | undefined;
  setDiscount: (percentBp: number | null, amountCents: number | null) => Promise<void>;
  setTargetPrice: (targetCents: number) => Promise<void>;
  setError: (message: string | null) => void;
}) {
  const [discountMode, setDiscountMode] = useState<RabattModus>("percent");
  const [discountPreviewRaw, setDiscountPreviewRaw] = useState("");
  /**
   * Bughunt 27.08.2026 (Antoine, Live-Screenshot): zwischen Blur/Klick und der Server-Antwort
   * blieb die Karte optisch unveraendert stehen - kein Hinweis, dass gerade neu gerechnet wird.
   * Lokaler Ladezustand, ausschliesslich fuer diesen Block (kein Einfluss auf Autosave o.ae.).
   */
  const [anpassenBusy, setAnpassenBusy] = useState(false);
  const [anpassenOffen, setAnpassenOffen] = useState(false);
  async function mitSpinner(fn: () => Promise<void>) {
    setAnpassenBusy(true);
    try {
      await fn();
    } finally {
      setAnpassenBusy(false);
    }
  }
  /**
   * A5 — liegt auf DIESER Variante ein Rabatt, den es zu entfernen gaebe? Bewusst an den
   * GESPEICHERTEN Feldern der Option abgelesen und nicht an `breakdown.discountCents > 0`:
   * Letzteres ist ein abgeleiteter Betrag und waere z. B. bei einem 0-%-Rabatt (gesetzt, aber
   * wirkungslos) falsch — der Knopf verschwaende, obwohl noch etwas hinterlegt ist.
   *
   * Bughunt 27.08.2026 (Antoine, Live-Screenshot): ein per Zielpreis ueber der Zwischensumme
   * erzeugter AUFSCHLAG setzt kein discountPercentBp/discountAmountCents (er lebt als eigene
   * ADJUSTMENT_SECTION-Zeile, siehe `adjustment`-Prop) — ohne diesen Zusatz zeigte die Karte gar
   * keinen "entfernen"-Knopf, der Aufschlag war unloeschbar ueber diese Karte.
   */
  const rabattHinterlegt = option?.discountPercentBp != null || option?.discountAmountCents != null || adjustment != null;
  const discountPreviewLine = useMemo(
    () => rabattVorschauZeile(discountPreviewRaw, discountMode, breakdown.subtotalCents),
    [discountPreviewRaw, discountMode, breakdown.subtotalCents],
  );
  /**
   * R34-4 (Konzept § 2 "Ehrliche Gesamt-Beschriftung im Blockiert-Zustand"): EXAKT dieselbe
   * Bedingung wie `menuPreisFehlt` in `builder.tsx`/`PositionsListe.tsx` (R34-1/R34-3),
   * gespiegelt auf demselben `option`-Prop dieser Karte — kein zweiter Rechenweg. Die Zahl
   * `breakdown.amountTotalCents` bleibt unveraendert (der 0-€-Menue-Anteil ist mathematisch
   * schon korrekt, s. Konzept § 2) — nur das Label wird ehrlich, statt einen fertigen
   * Gesamtbetrag zu behaupten, waehrend ein Pflichtfeld noch fehlt.
   */
  // sql/445: gilt fuer beide Pauschal-Modi — `total` (je Gast) wie `total_event` (ganze
  // Veranstaltung). In beiden Faellen steht ein Modus ohne den Betrag, den er braucht.
  useEffect(() => {
    if (rabattHinterlegt) setAnpassenOffen(true);
  }, [rabattHinterlegt]);
  const menuPreisFehlt = istPauschal(option?.pricingMode) && option?.menuTotalPriceCents == null;
  return (
    <Card className="space-y-4 p-5">
      {/* Builder v3 (Entwurf 31a, Anordnung): Der Entwurf schreibt die Variante an den
          Kopf der Zusammenfassung. Bei zwei Varianten mit aehnlichen Summen ist das der
          Unterschied zwischen "welche Zahl sehe ich hier gerade" und Nachschauen. */}
      {/* Mockup v3: „Gesamt" steht oben, gross und in der Akzentfarbe — darunter der Rechenweg.
          Rechts die Gaestezahl der Variante (vorher ihr Titel bzw. Betrag). */}
      <div>
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-beleg-panel text-on-background dark:text-inverse-on-surface">{menuPreisFehlt ? "Gesamt — bisher, ohne Menü" : "Gesamt"}</h3>
          {option && (
            <span className="text-beleg-rechenweg text-on-surface-variant dark:text-secondary-fixed-dim">
              {option.title ? `${option.title} · ` : ""}
              {option.guestCount} Gäste
            </span>
          )}
        </div>
        {/* E2E (Golden Path Anfrage->Angebot): einzige verlaessliche Stelle fuer den Gesamtbetrag. */}
        <p data-testid="builder-gesamtsumme" className="mt-1 text-[24px] font-bold tabular-nums tracking-[-0.01em] text-primary dark:text-primary-fixed-dim">
          {money(breakdown.amountTotalCents)}
        </p>
      </div>
      {/* Mockup v3: die Abschnittssummen stehen im Fuß des Dokuments. Hier erscheinen sie nur,
          wenn ein Rabatt oder eine Anpassung den Rechenweg bis zur Summe braucht. */}
      {(breakdown.discountCents > 0 || breakdown.adjustmentCents !== 0) && (
      <dl className="space-y-2 font-body-sm text-body-sm">
        {sectionSubtotals.map((s) => (
          <Row key={s.key} label={s.label} value={money(s.cents)} muted />
        ))}
        {sectionSubtotals.length > 0 && <div className="my-1 border-t border-outline-variant/20 dark:border-outline/20" />}
        {/* Zwischensumme nur, wenn danach noch etwas passiert. Ohne Rabatt und ohne
            Anpassung ist sie Zeichen fuer Zeichen die Gesamtzeile darunter - der Entwurf
            zeigt sie deshalb nicht. Sobald ein Rabatt greift, ist sie der Ausgangswert
            des Rechenwegs und steht wieder da. */}
        {(breakdown.discountCents > 0 || breakdown.adjustmentCents !== 0) && (
          <Row label="Zwischensumme" value={money(breakdown.subtotalCents)} />
        )}
        {breakdown.discountCents > 0 && <Row label="Rabatt" value={`− ${money(breakdown.discountCents)}`} tone="green" />}
        {breakdown.adjustmentCents !== 0 && <Row label="Preisanpassung" value={money(breakdown.adjustmentCents)} />}
      </dl>
      )}

      {/* Builder v3 (Entwurf 31a, Anordnung): Netto und USt stehen im Entwurf abgesetzt
          unter der Gesamtsumme — und die USt-Zeile nennt, WORAUF der Satz laeuft
          ("USt 7 % auf 1.464,00 €"). Genau diese Frage stellt der Betreiber beim Pruefen.
          Gerechnet wird hier nichts Neues: `netCents` und `grossCents` je Satz kommen
          fertig aus packages/pricing (VatBucket), Netto ist deren Summe. */}
      {breakdown.vatBreakdown.length > 0 && (
        <div className="space-y-1.5 rounded-lg bg-surface-container-high/60 px-3 py-2.5 text-beleg-rechenweg dark:bg-surface-variant/15">
          <div className="flex items-center justify-between">
            <span className="text-on-surface-variant dark:text-secondary-fixed-dim">Netto</span>
            <span className="text-beleg-betrag text-on-background dark:text-inverse-on-surface">
              {money(breakdown.vatBreakdown.reduce((s, b) => s + b.netCents, 0))}
            </span>
          </div>
          {/* Nur Saetze, auf die tatsaechlich etwas entfaellt. Am echten Angebot (Louisa
              Egger, 03.08.2026) stand sonst „USt 0 % auf 0,00 € — 0,00 €": eine Zeile ohne
              jede Aussage, erzeugt von einer optionalen Position ohne Preis (K1.0). Der
              0-%-Eimer entsteht korrekt in der Engine; anzuzeigen ist er trotzdem nur,
              wenn er etwas traegt. */}
          {breakdown.vatBreakdown
            .filter((b) => b.grossCents > 0)
            .map((b) => (
              <div key={b.rateBp} className="flex items-center justify-between gap-2 text-on-surface-variant dark:text-secondary-fixed-dim">
                <span className="min-w-0">
                  USt {vatLabel(b.rateBp)} <span className="text-on-surface-variant/70 dark:text-secondary-fixed-dim">auf {money(b.grossCents)}</span>
                </span>
                <span className="shrink-0">{money(b.vatCents)}</span>
              </div>
            ))}
        </div>
      )}

      <div className="rounded-lg bg-primary/9 p-3">
        <div className="flex items-center justify-between">
          <span className="text-beleg-notiz text-on-background dark:text-inverse-on-surface">Preis pro Person</span>
          <span className="text-beleg-notiz font-bold text-primary">{money(breakdown.perPersonCents)}</span>
        </div>
      </div>

      {option && option.minimumSpendCents != null && breakdown.amountTotalCents < option.minimumSpendCents && (
        <div className="rounded-lg bg-[#F0EAE0] px-3 py-2 font-body-sm text-body-sm text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
          Unter Mindestumsatz ({money(option.minimumSpendCents)}).
        </div>
      )}

      {/* Referenz-Angebote P3.5 (§5.10): dezente Hinweiszeile bei KI-erzeugten Varianten -
          Pillar + Stilprofil-Version, Budget Ist/Ziel pro Gast, warnings als Chips. Kein
          weiterer Umbau des Builders. */}
      {option && option.source === "ai_generated" && option.aiMeta && (
        <div className="space-y-1.5 rounded-lg bg-surface-container-high/60 px-3 py-2 font-body-sm text-body-sm text-on-surface-variant dark:bg-surface-variant/10">
          {/* Bughunt 27.08.2026: aiMeta.pillar wird seit dem Umbau auf den Komponisten nur noch
              geschrieben, wenn die Bewirtungsart der Anfrage TATSAECHLICH bekannt ist (siehe
              schreibeVarianteInOption in lib/kompositions-lauf.ts) - vorher fiel dieses Feld
              still auf "Event (im Haus)" zurueck, obwohl es das gar nicht wusste, und
              widersprach damit der Rueckfrage nach dem Veranstaltungsort. Ohne bekannten Wert
              zeigt diese Zeile lieber gar nichts als eine geratene Antwort. */}
          {(option.aiMeta.pillar || option.aiMeta.styleProfileVersion != null) && (
            <div className="flex items-center gap-1.5">
              <Icon name="auto_awesome" className="shrink-0 text-[13px] text-primary" />
              <span className="font-medium text-on-background dark:text-inverse-on-surface">
                {option.aiMeta.pillar === "catering" ? "Catering (außer Haus)" : option.aiMeta.pillar === "event" ? "Event (im Haus)" : null}
                {option.aiMeta.pillar && option.aiMeta.styleProfileVersion != null && " · "}
                {option.aiMeta.styleProfileVersion != null && `Stilprofil v${option.aiMeta.styleProfileVersion}`}
              </span>
            </div>
          )}
          {/* E2.1: Solange MAESTRO den Betrieb kaum kennt, steht das AN der Karte, an der
              gearbeitet wird - nicht in einer Einstellung, die niemand liest. Der Hinweis
              verschwindet von selbst (5 versendete Angebote oder 5 Unterlagen). */}
          {option.aiMeta.kaltstart && (
            <div className="flex items-start gap-1.5 rounded-md bg-secondary/70 px-2 py-1.5 text-[12px] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
              <Icon name="info" className="mt-px shrink-0 text-[13px]" />
              <span>{option.aiMeta.kaltstart}</span>
            </div>
          )}
          {option.aiMeta.targetPerGuestCents != null && option.aiMeta.actualPerGuestCents != null && (
            <div className="text-[12px]">
              Budget: {money(option.aiMeta.actualPerGuestCents)} / Gast (Ziel {money(option.aiMeta.targetPerGuestCents)})
            </div>
          )}
          {(option.aiMeta.warnings?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-1 pt-0.5">
              {option.aiMeta.warnings!.map((w, i) => (
                <span
                  key={i}
                  className="rounded-full bg-[#F0EAE0] px-2 py-0.5 text-[10.5px] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
                >
                  {w}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preis anpassen (8d) -- EIN Regler statt drei Feldern: Segmented Rabatt %/€/Zielpreis +
          Live-Vorschau. Preislogik unveraendert: je Modus weiterhin setDiscount()/setTargetPrice(). */}
      {/* Mockup v3: „Preis anpassen" ist aufklappbar — offen, sobald ein Rabatt/eine Anpassung
          hinterlegt ist (sonst saehe man das „entfernen" nicht). Dieselben drei Wege wie bisher. */}
      <div className="space-y-2 border-t border-outline-variant/30 pt-3 dark:border-outline/30">
        <button
          type="button"
          aria-expanded={anpassenOffen}
          data-testid="builder-preis-anpassen"
          onClick={() => setAnpassenOffen((v) => !v)}
          className="flex w-full items-center gap-1.5 rounded-md text-left font-label-md text-label-md text-on-surface-variant hover:text-on-background dark:text-secondary-fixed-dim dark:hover:text-inverse-on-surface"
        >
          <Icon name="tune" className="text-[17px]" />
          Preis anpassen
          {anpassenBusy && (
            <Icon name="progress_activity" className="animate-spin text-[13px] normal-case tracking-normal text-primary" />
          )}
          <Icon name={anpassenOffen ? "expand_less" : "expand_more"} className="ml-auto text-[18px]" />
        </button>
        {anpassenOffen && (
        <>
        <div className="flex gap-1 rounded-lg bg-surface-container-high p-1 dark:bg-surface-variant/20">
          {(
            [
              { key: "percent", label: "Rabatt %" },
              { key: "amount", label: "Rabatt €" },
              { key: "target", label: "Zielpreis" },
            ] as const
          ).map((m) => (
            <button
              key={m.key}
              type="button"
              disabled={anpassenBusy}
              onClick={() => {
                setDiscountMode(m.key);
                setDiscountPreviewRaw("");
              }}
              className={`flex-1 rounded-md px-2 py-1.5 font-label-md text-[11.5px] transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                discountMode === m.key
                  ? "bg-surface-container-lowest text-primary shadow-sm dark:bg-night-card"
                  : "text-on-surface-variant dark:text-secondary-fixed-dim"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Input
            disabled={anpassenBusy}
            // L3: an die GELADENE Option (option?.id) koppeln, nicht an activeId. Beim
            // Varianten-Wechsel wechselt activeId sofort, waehrend option/breakdown erst
            // nach loadFull() nachziehen -- ein activeId-Key remountete das Feld dann mit
            // dem defaultValue der noch alten Variante (Wert-Leak). option?.id aendert sich
            // erst nach dem Laden, sodass defaultValue immer zur echten Variante passt.
            /**
             * A5: der GESPEICHERTE Rabattwert haengt zusaetzlich im Key. Das Feld ist
             * unkontrolliert (`defaultValue`) — ohne diesen Teil bliebe nach dem Loeschen der
             * alte Wert stehen, waehrend die Zusammenfassung daneben schon „kein Rabatt" zeigt.
             *
             * Ein blosser Klick-Zaehler haette das NICHT geloest: er zaehlte im Moment des
             * Klicks hoch, also BEVOR die Server-Antwort mit dem geleerten Rabatt da ist — das
             * Feld haette sich mit dem noch alten `defaultValue` neu aufgebaut. Der Key muss an
             * dem haengen, was sich tatsaechlich aendert.
             *
             * Warum das L3 (unten) nicht wieder aufreisst: `option.discountPercentBp`/
             * `discountAmountCents` aendern sich ausschliesslich als Folge eines Schreibvorgangs,
             * und der wird nur aus `onBlur` oder aus dem „Rabatt entfernen"-Knopf angestossen —
             * in beiden Faellen hat das Feld den Fokus bereits verloren. Ein Remount mitten im
             * Tippen ist damit ausgeschlossen. Aenderungen an `option`, die den Rabatt nicht
             * betreffen (Autosave, Preismodus, Menuepreis), lassen den Key unberuehrt.
             */
            key={`${discountMode}-${option?.id ?? activeId}-${option?.discountPercentBp ?? ""}-${option?.discountAmountCents ?? ""}`}
            data-testid="builder-rabatt-feld"
            className="pr-8 disabled:cursor-not-allowed disabled:opacity-60"
            // M5: Wert bei Fokus markieren, damit Tippen ihn ersetzt statt anzuhaengen.
            onFocus={(e) => e.target.select()}
            placeholder={discountMode === "percent" ? "z. B. 10" : discountMode === "amount" ? "z. B. 150,00" : "z. B. 2500,00"}
            defaultValue={
              discountMode === "percent"
                ? option?.discountPercentBp != null
                  ? String(option.discountPercentBp / 100)
                  : ""
                : discountMode === "amount"
                  ? option?.discountAmountCents != null
                    ? centsToEuros(option.discountAmountCents)
                    : ""
                  : breakdown.discountCents > 0 || breakdown.adjustmentCents !== 0
                    ? centsToEuros(breakdown.amountTotalCents)
                    : ""
            }
            onChange={(e) => setDiscountPreviewRaw(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            onBlur={(e) => {
              const v = e.target.value.trim();
              setDiscountPreviewRaw("");
              /**
               * A5 (UX-Polish 24.08.2026) — LEERES FELD = KEINE AENDERUNG, in ALLEN DREI MODI.
               *
               * Bis hierher riefen `percent` und `amount` bei leerem Feld sofort
               * `setDiscount(null, null)`. Das war als Validierungsverhalten gemeint (Kommentar
               * „N-F2": lieber ausdruecklich loeschen als still auf 0 clampen), traf aber jeden,
               * der das Feld nur LEERT, UM NEU ZU TIPPEN: ein Klick daneben, ein Tab, ein
               * Wechsel des Modus-Knopfes — und `POST /api/offers/:id/discount` loeschte den
               * Rabatt serverseitig, die Summe sprang auf unrabattiert zurueck. Ein
               * Zwischenzustand des Tippens darf keine Datenaenderung ausloesen.
               *
               * `target` verhielt sich hier schon immer so (`else if (v)`) — jetzt sind alle
               * drei Modi gleich, statt dass einer von dreien stillschweigend loescht.
               *
               * Geloescht wird nur noch ueber den ausdruecklichen Knopf unter dem Feld.
               */
              if (v === "") return;
              // N-F2 (unveraendert): ungültige Eingabe als Validierungsfehler melden statt still
              // auf 0 zu clampen. M9: negative Prozent-Eingabe weiterhin auf 0 klemmen (kein
              // Aufschlag).
              if (discountMode === "percent") {
                const pct = parseGermanDecimal(v);
                if (pct == null) return setError("Ungültiger Rabatt-Prozentwert.");
                void mitSpinner(() => setDiscount(Math.round(Math.max(0, pct) * 100), null));
              } else if (discountMode === "amount") {
                const cents = parseGermanEurosToCents(v);
                if (cents == null) return setError("Ungültiger Rabattbetrag.");
                void mitSpinner(() => setDiscount(null, cents));
              } else {
                const cents = parseGermanEurosToCents(v);
                if (cents == null) return setError("Ungültiger Zielpreis.");
                void mitSpinner(() => setTargetPrice(cents));
              }
            }}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-body-sm text-on-surface-variant">
            {discountMode === "percent" ? "%" : "€"}
          </span>
        </div>
        <p className="font-body-sm text-body-sm text-on-surface-variant/70 dark:text-secondary-fixed-dim">
          {discountPreviewLine ??
            (discountMode === "target"
              ? `Unter der Zwischensumme (${money(breakdown.subtotalCents)}) → Rabatt. Darüber → Preisanpassung (Aufschlag).`
              : "Wert eingeben, dann Enter/Tab zum Übernehmen. Leeres Feld ändert nichts.")}
        </p>
        {/**
         * A5 — die AUSDRUECKLICHE Geste zum Loeschen. Sie ersetzt das frühere „Feld leeren
         * genügt": Loeschen ist eine Entscheidung und braucht einen Klick, den man nicht aus
         * Versehen macht — ein leeres Feld ist dagegen der Normalzustand mitten im Tippen.
         *
         * Der Knopf steht bewusst DORT, wo der hinterlegte Rabatt genannt wird, statt als
         * vierter Knopf in die Modus-Leiste zu wandern: er gehoert zum Zustand („hier liegt ein
         * Rabatt"), nicht zur Eingabeart. Sichtbar ist er nur, solange es etwas zu loeschen
         * gibt (`rabattHinterlegt`, an den gespeicherten Feldern abgelesen) — ein Knopf, der
         * nichts tun kann, gehoert nicht auf den Bildschirm.
         */}
        {rabattHinterlegt && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-body-sm text-body-sm text-on-surface-variant/70 dark:text-secondary-fixed-dim">
              {adjustment
                ? `Als Preisanpassung hinterlegt: + ${money(breakdown.adjustmentCents)}`
                : breakdown.discountCents > 0
                  ? `Als Rabatt hinterlegt: − ${money(breakdown.discountCents)}`
                  : "Rabatt hinterlegt"}
            </p>
            <button
              type="button"
              data-testid="builder-rabatt-entfernen"
              disabled={anpassenBusy}
              className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 font-label-md text-[11.5px] text-on-surface-variant transition-colors hover:bg-error-container hover:text-error disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:hover:bg-error/20 dark:hover:text-[#e39b92]"
              title={
                adjustment
                  ? "Die hinterlegte Preisanpassung entfernen — die Summe sinkt danach auf den Betrag ohne Aufschlag"
                  : "Den hinterlegten Rabatt entfernen — die Summe steigt danach auf den unrabattierten Betrag"
              }
              onClick={() => {
                void mitSpinner(() => setDiscount(null, null));
                setDiscountPreviewRaw("");
              }}
            >
              <Icon name="backspace" className="text-[14px]" /> {adjustment ? "Preisanpassung entfernen" : "Rabatt entfernen"}
            </button>
          </div>
        )}
        {adjustment && <p className="font-body-sm text-body-sm text-on-surface-variant/70 dark:text-secondary-fixed-dim">Anpassung (Aufschlag): {money(adjustment.unitPriceCents)}</p>}
        </>
        )}
      </div>
    </Card>
  );
}
